<template>
  <div>
    <EppHeader
      heading="Events"
      subtitle="Manage events and registrations"
      icon="calendar_today"
      :iconOutlined="true"
    />
    <TabNavigation
      :items="tabs.map((tab) => ({ tab: tab.type + (tab.displaySuffix || ' events') }))"
      class="tab-navigation"
    >
      <template
        #extra
        v-if="['Super Admin', 'Event Admin'].includes(loggedInUserRole)"
      >
        <a
          class="v-tab"
          href="https://education.nsw.gov.au/inside-the-department/skills-initiatives-and-programs/educational-pathways-program-/referral-information---program-initiatives"
          target="_blank"
        >Referrals</a>
      </template>
      <template
        v-for="(tab, i) in tabs"
        #[tab.content]
      >
        <!-- Custom component tab -->
        <component
          v-if="tab.component"
          :is="tab.component"
          :key="i"
        />

        <v-container
          v-else
          :key="i"
          fluid
        >
          <v-row v-if="showEventCreationSuccessMessage || showEventUpdateSuccessMessage">
            <v-col>
              <Alert
                showAlert
                type="success"
                :text="createSuccessMessage"
                inPage
                :backgroundColour="true"
                v-if="showEventCreationSuccessMessage"
                allowDismiss
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <div class="d-flex flex-row justify-space-between align-center">
                <div :style="{ width: tab.type === 'past' ? '75%' : tab.type === 'upcoming' ? '80%' : '100%' }">
                  <AdsTextField
                    v-model="search"
                    prepend-inner-icon="mdi-magnify"
                    background-color="white"
                    placeholder="Search"
                    label="Search"
                    single-line
                    hide-details
                    clearable
                    :outlined="true"
                  />
                </div>
                <div v-if="tab.type !== 'cancelled'" style="width: 25%">
                  <AdsButton
                    v-if="tab.type == 'upcoming' &&
                      ['Super Admin', 'Event Admin'].includes(
                        loggedInUserRole
                      )
                    "
                    large
                    icon="add_circle_outline"
                    buttonText="Create event"
                    class="ml-2"
                    @click="toggleCreateDialog()"
                  />
                  <v-sheet v-if="tab.type == 'past'" class="d-flex outlined border" outlined>
                    <v-btn
                      color="primary"
                      icon
                      tile
                      class="mr-1"
                      @click="getPreviousEvents()"
                    >
                      <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                    <v-text-field
                      v-model="dateRange"
                      label="Date range"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      disabled
                      style="width: 100%"
                      hide-details
                    />
                    <v-btn
                      color="primary"
                      icon
                      tile
                      :class="{ 'disabled-btn': isPastEventsEndDatAtToday}"
                      :disabled="isPastEventsEndDatAtToday"
                      @click="getNextEvents()"
                    >
                      <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                  </v-sheet>
                </div>
                <div>
                  <FullscreenDialog
                    v-if="showCreateDialog"
                    title="Create event"
                    v-model="showCreateDialog"
                    :showCloseButton="false"
                  >
                    <template #content>
                      <CreateEvent
                        v-if="showCreateDialog"
                        @eventAdded="toggleCreateDialog"
                      />
                    </template>
                  </FullscreenDialog>
                </div>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <div class="d-flex align-center flex-wrap">
                <label class="mr-4 text-no-wrap">Filter by:</label>
                <template v-for="(filter, key) in filters">
<!--                  <DateChipFilter-->
<!--                    v-if="filter.type == 'date'"-->
<!--                    v-model="selectedFilters[key]"-->
<!--                    :key="key"-->
<!--                    class="ml-1"-->
<!--                    :name="filter.label"-->
<!--                    :items="filter.items"-->
<!--                    heading="Date"-->
<!--                  />-->
                  <ChipFilter
                    v-if="filter.label !== 'Schools' && filter.type !== 'date'"
                    v-model="selectedFilters[key]"
                    :key="key"
                    class="chipFilter ml-1"
                    :name="filter.label"
                    :type="filter.type"
                    :items="filter.items"
                    :heading="filter.label"
                  />

                  <ChipFilter
                    v-if="filter.label == 'Schools'"
                    v-model="selectedFilters[key]"
                    type="checkbox"
                    :items="transformedSchoolData"
                    :multi-level="true"
                    heading="Schools"
                    name="Schools"
                    :key="key"
                  />
                </template>
                <AdsButton
                  buttonText="Clear all"
                  class="clearAll"
                  tertiary
                  @click="clearAll"
                />
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              v-if="tab.type"
              cols="12"
            >
              <template>
                <div v-if="!grouped_events[tab.type].loading && getEventsForTab(tab.type).length === 0">
                  <template v-if="tab.type==='past'">
                    There are no events between <strong>{{ pastEventStartDate | formatDate }}</strong> and <strong>{{ pastEventsEndDate | formatDate }}</strong>
                  </template>
                  <template v-else>
                    There are no {{ tab.type }} events to display.
                  </template>
                </div>
                <div v-else>
                  <v-row class="mb-2">
                    <v-col
                      cols="7"
                      class="pt-5"
                    >
                      <div class="d-flex justify-start w-100">
                        <div class="mt-2">
                          Displaying {{ getEventsForTab(tab.type).length }} {{ tab.type }} event<span v-if="getEventsForTab(tab.type).length> 1">s</span>
                          <span v-if="tab.type==='past'"> between <strong> {{ pastEventStartDate | formatDateForLabel }} </strong> and <strong>{{ pastEventsEndDate | formatDateForLabel }}</strong></span>
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="5" align="right" justify="center" class="pt-5 pr-2" >
                      <div class="d-flex justify-end">
                        <div class="d-flex align-center justify-end mr-1">
                          <label class="mr-4 text-no-wrap">Sort by:</label>
                          <AdsSelect
                            v-model="selectedSort"
                            :items="sortOptions"
                            item-text="name"
                            item-value="id"
                            dense
                            hide-details
                            background-color="white"
                            :return-object="true"
                          />
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                  <div v-if="grouped_events[tab.type].loading">
                    <v-progress-circular
                      color="primary accent-4"
                      indeterminate
                      rounded
                      height="6"
                      class="mr-5"
                    />
                    Loading {{ tab.type }} events...
                  </div>
                  <EventList
                    v-else
                    :events="getEventsForTab(tab.type)"
                    :schools="schools"
                    :list-type="tab.type"
                    @onUpdate="(event, action) => fetchByType(tab.type, event, action)"
                  />
                </div>
              </template>
            </v-col>
          </v-row>
          <Alert
            type="success"
            v-if="showEventUpdateSuccessMessage"
            :text="editSuccessMessage"
            :inPage="false"
            :backgroundColour="true"
            show-alert
            allowDismiss
          />
        </v-container>
      </template>
    </TabNavigation>
  </div>
</template>

<script>
import {
  ADS_Colors,
  AdsButton,
  AdsTextField,
  ChipFilter,
  FullscreenDialog,
  AdsSelect,
  Alert,
} from '@nswdoe/doe-ui-core';

import { useAuthStore } from '@/stores/auth';
import { useTermsStore } from '@/stores/terms';
import { useFormatsStore } from '@/stores/formats';
import { useAudiencesStore } from '@/stores/audiences';
import { usePresenterTypesStore } from '@/stores/presenterTypes';
import { useEventStore } from '@/stores/eventStore';

import DateChipFilter from '@/components/DateChipFilter';
import TabNavigation from '@/components/TabNavigation';
import EventCard from '@/components/EventCard';
import CreateEvent from '@/components/CreateEvent';
import EditEvent from '@/components/EditEvent';
import StudentRegistration from '@/components/StudentRegistration';
import ParticipantList from '@/components/ParticipantList';
import EventList from '@/components/EventList/index.vue';
import { useAppSettingsStore } from '@/stores/appSettings';

export default {
  name: 'EppEvents',
  title: 'EPP Initiative Management Application - Events',
  components: {
    EventList,
    AdsButton,
    AdsSelect,
    AdsTextField,
    ChipFilter,
    DateChipFilter,
    FullscreenDialog,
    TabNavigation,
    EventCard,
    CreateEvent,
    EditEvent,
    StudentRegistration,
    Alert,
    ParticipantList
  },
  data() {
    return {
      ADS_Colors,
      selectedSort: { id: 'date', name: 'Date' },
      sortOptions: [
        { id: 'date', name: 'Date' },
        { id: 'alphabetically', name: 'Alphabetically' },
        { id: 'status', name: 'Status' },
      ],
      showEventCreationSuccessMessage: false,
      showEventUpdateSuccessMessage: false,
      editSuccessMessage: '',
      selectedFilters: {
        initiative_types: [],
        category: [],
        activity_type: [],
        schools: [],
        date: null,
        term: [],
        format: [],
        audiences: [],
        vet_combined: [],
        media_suitable: []
      },
      search: '',
      showCreateDialog: false,
      events: [],
      grouped_events: {
        upcoming: {
          events: [],
          loading: false,
          pagination: {
            page: 1,
            perPage: 10,
            totalPages: 1,
          }
        },
        past: {
          events: [],
          loading: false,
          pagination: {
            page: 1,
            perPage: 10,
            totalPages: 1,
          }
        },
        cancelled: {
          events: [],
          loading: false,
          pagination: {
            page: 1,
            perPage: 10,
            totalPages: 1,
          }
        }
      },
      schools: [],
      transformedSchoolData: [],
      regions: [],
      schoolGroups: [],
      eventToEdit: {},
      dulplicateEvent: false,
      eventTitle: '',
      changed: false,
      today: this.$options.filters.formatDate(new Date()),
      pastEventsEndDate: new Date(),
    };
  },
  computed: {
    dateRange() {
      return this.$options.filters.formatDateForLabel(this.pastEventStartDate) + ' - ' + this.$options.filters.formatDateForLabel(this.eventStore.pastEventsEndDate)
    },
    pastEventStartDate() {
      const startDate = new Date(this.eventStore.pastEventsEndDate);
      startDate.setMonth(startDate.getMonth() - 3);
      return startDate
    },
    isPastEventsEndDatAtToday () {
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const endDate = new Date(this.eventStore.pastEventsEndDate)
      endDate.setHours(0, 0, 0, 0)

      return endDate.getTime() === today.getTime()
    },
    filteredUpcomingEvents() {
      const events = this.grouped_events.upcoming.events.filter(event => {
        return this.filterEvent(event);
      });
      return events?.sort(this.getSortFunction('upcoming')) || [];
    },
    filteredPastEvents() {
      const events = this.grouped_events.past?.events.filter(event => {
        return this.filterEvent(event);
      });
      return events?.sort(this.getSortFunction('past')) || [];
    },
    filteredCancelledEvents() {
      const events = this.grouped_events.cancelled?.events.filter(event => {
        return this.filterEvent(event);
      });
      return events?.sort(this.getSortFunction('past')) || [];
    },
    createSuccessMessage() {
      return `Your event ${this.eventTitle} has been successfully created`;
    },
    initiativeTypes() {
      return this.appSettingsStore.activeInitiatives.map(item => item.settingKey).sort((a, b) => a.localeCompare(b))
    },
    categories() {
      return this.appSettingsStore.activeCategories.map(item => item.settingKey).sort((a, b) => a.localeCompare(b))
    },
    activityTypes() {
      return this.appSettingsStore.activeActivityTypes.map(item => item.settingKey).sort((a, b) => a.localeCompare(b))
    },
    terms() {
      const terms = useTermsStore();
      return terms.items;
    },
    formats() {
      const formats = useFormatsStore();
      return formats.items;
    },
    audiences() {
      const audiences = useAudiencesStore();
      return audiences.items;
    },
    presenterTypes() {
      const presenterTypes = usePresenterTypesStore();
      return presenterTypes.items;
    },
    loggedInUserRole() {
      const auth = useAuthStore();
      return auth.role;
    },
    tabs() {
      if (!this.loggedInUserRole) {
        return [];
      }
      const tabs = [
        { type: 'upcoming', loading: false, content: 'content1' },
        { type: 'past', loading: false, content: 'content2'},
        { type: 'cancelled', loading: false, content: 'content3' },
        {
          type: 'student',
          loading: false,
          content: 'content4',
          displaySuffix: ' participants',
          component: 'ParticipantList'
        },
      ];
      return tabs;
    },
    filters() {
      const filters = {
        initiative_types: {
          label: 'Initiative types',
          items: this.initiativeTypes,
        },
        category: {
          label: 'Event category',
          items: this.categories,
        },
        vet_combined: {
          label: 'VA combined',
          items: ['Yes', 'No'],
        },
        media_suitable: {
          label: 'Media Suitable',
          items: ['Yes', 'No'],
        },
        activity_type: {
          label: 'Activity type',
          items: this.activityTypes,
        },
        schools: {
          label: 'Schools',
          items: this.schools,
        },
        date: {
          label: 'Date',
          type: 'date',
        },
        term: {
          label: 'Term',
          items: this.terms,
        },
        format: {
          label: 'Format',
          items: this.formats,
        },
        audiences: {
          label: 'Audiences',
          items: this.audiences,
        },
      };
      Object.values(filters).forEach((filter) => {
        if (filter.type !== 'date') {
          filter.items = filter.items.map((name) => ({
            id: name,
            name,
          }));
        }
        if (!filter.type) {
          filter.type = 'checkbox';
        }
      });

      return filters;
    },
  },
  setup() {
    const eventStore = useEventStore();
    const appSettingsStore = useAppSettingsStore()

    return { eventStore, appSettingsStore }
  },
  mounted() {
    this.fetchEvents();
    Promise.all([
      this.$api.schools.fetchNestedData().then((results) => {
        this.schools = results;
        this.transformedSchoolData = this.transformSchoolData(results);
      }),
    ]).catch(() => { });
  },
  filters: {
    formatDate: function (timestamp) {
      if (!timestamp) return '';

      const date = new Date(Number(timestamp));
      const day = ('0' + date.getDate()).slice(-2);
      const month = ('0' + (date.getMonth() + 1)).slice(-2);
      const year = date.getFullYear();

      return `${year}-${month}-${day}`;
    },
    formatDateForLabel: function (timestamp) {
      if (!timestamp) return '';

      const date = new Date(Number(timestamp));
      const day = ('0' + date.getDate()).slice(-2);
      const month = ('0' + (date.getMonth() + 1)).slice(-2);
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    },
  },
  methods: {
    getPreviousEvents() {
      this.pastEventsEndDate = new Date(this.pastEventStartDate)
      this.eventStore.setPastEventsEndDate(new Date(this.pastEventStartDate));

      this.fetchPastEvents()
    },
    getNextEvents() {
      const endDate = new Date(this.eventStore.pastEventsEndDate);
      endDate.setMonth(endDate.getMonth() + 3);
      this.eventStore.setPastEventsEndDate(endDate);
      this.fetchPastEvents()
    },
    getEventsForTab(tab) {
      return tab === 'upcoming' ? this.filteredUpcomingEvents : tab === 'past' ? this.filteredPastEvents : this.filteredCancelledEvents;
    },
    filterEvent(event) {
      // If there's a search query, perform a search
      if (this.search) {
        // Convert search query to lowercase
        const searchQuery = this.search.toLowerCase();
        let foundMatch = false;

        // Iterate through event keys to find matches
        for (const key in event) {
          const value = event[key];
          // If value is a string and includes search query, mark as found
          if (
            typeof value === 'string' &&
            value.toLowerCase().includes(searchQuery)
          ) {
            foundMatch = true;
            break;
          } else if (Array.isArray(value)) {
            // If value is an array, search within array for a match
            if (
              value.some(
                (item) =>
                  typeof item === 'string' &&
                  item.toLowerCase().includes(searchQuery)
              )
            ) {
              foundMatch = true;
              break;
            }
          }
        }

        // Skip this event if it doesn't match the search query
        if (!foundMatch) return;
      }

      if (
        this.selectedFilters.initiative_types.length &&
        !this.selectedFilters.initiative_types.includes(event.initiative_type)
      )
        return;
      if (
        this.selectedFilters.category.length &&
        !this.selectedFilters.category.includes(event.category)
      )
        return;

      const media_suitable = this.selectedFilters.media_suitable;
      if (
        media_suitable.length === 1 &&
        (!!event.media_suitable !== (media_suitable[0] === 'Yes'))
      ) return;

      if (
        this.selectedFilters.vet_combined.length > 0 &&
        (this.selectedFilters.vet_combined[0] === 'Yes') !==
        event.combined_event
      )
        return;
      if (
        this.selectedFilters.activity_type.length &&
        !this.selectedFilters.activity_type.includes(event.activity_type)
      )
        return;
      if (
        this.selectedFilters.schools.length &&
        !event.schools.some((school) =>
          this.selectedFilters.schools.includes(school)
        )
      )
        return;
      if (
        this.selectedFilters.term.length &&
        !this.selectedFilters.term.includes(event.term)
      )
        return;
      if (
        this.selectedFilters.format.length &&
        !this.selectedFilters.format.includes(event.format)
      )
        return;
      if (
        this.selectedFilters.audiences.length &&
        !event.audiences.some((audience) =>
          this.selectedFilters.audiences.includes(audience)
        )
      )
        return;
      // Check date filter
      if (this.selectedFilters.date) {
        // Convert event start and end dates to Date objects
        const eventStartDate = new Date(event.start_date);
        const eventEndDate = new Date(event.end_date);

        // Check if selected date filter includes a date range
        if (this.selectedFilters.date.includes(' - ')) {
          // Handle date range by splitting the start and end dates
          const [startDateFilter, endDateFilter] = this.selectedFilters.date
            .split(' - ')
            .map((dateStr) => new Date(dateStr));
          startDateFilter.setHours(0, 0, 0, 0);
          endDateFilter.setHours(0, 0, 0, 0);

          // Check if the event falls outside the selected date range
          if (
            eventEndDate < startDateFilter ||
            eventStartDate > endDateFilter
          )
            return;
        } else {
          // Handle single date
          const dateFilter = new Date(this.selectedFilters.date);
          dateFilter.setHours(0, 0, 0, 0);

          // Check if the selected date falls outside the event's date range
          if (dateFilter < eventStartDate || dateFilter > eventEndDate)
            return;
        }
      }
      return true;
    },
    transformSchoolData(data) {
      return data
        .map((item) => {
          const newItem = { ...item };
          delete newItem.region_id;
          delete newItem.last_updated;
          delete newItem.updated_by;
          delete newItem.school_group_id;
          delete newItem.category;

          if (newItem.groups) {
            newItem.children = this.transformSchoolData(newItem.groups);
            delete newItem.groups;
          }

          if (newItem.schools) {
            newItem.children = newItem.schools.map((school) => {
              const newSchool = { ...school };
              delete newSchool.region_id;
              delete newSchool.last_updated;
              delete newSchool.updated_by;
              delete newSchool.school_group_id;
              delete newSchool.category;

              // Add (Inactive) to school name if status is Inactive
              if (newSchool.status === 'Inactive') {
                newSchool.name = `${newSchool.name} (Inactive)`;
              }

              return newSchool;
            });
            delete newItem.schools;
          }
          return newItem;
        })
        .filter((item) => item.children && item.children.length > 0);
    },
    fetchUpcomingEvents() {
      this.grouped_events.upcoming.loading = true;
      this.$api.events.fetch(null, 'upcoming').then((response) => {
        this.grouped_events.upcoming.events = response.events.sort(this.getSortFunction('upcoming'));
        this.eventStore.setUpcomingEvents(response.events.sort(this.getSortFunction('upcoming')));
        this.grouped_events.upcoming.loading = false;
      });
    },
    fetchPastEvents() {
      this.grouped_events.past.loading = true;
      const dates = {
        startDate: this.pastEventStartDate.toISOString().split('T')[0],
        endDate: this.eventStore.pastEventsEndDate.toISOString().split('T')[0]
      }
      this.$api.events.fetch(null, 'past', dates).then((response) => {
        this.grouped_events.past.events = response.events;
        this.eventStore.setPastEvents(response.events.sort(this.getSortFunction('past')));
        this.grouped_events.past.loading = false;
      });
    },
    fetchCancelledEvents() {
      this.grouped_events.cancelled.loading = true;
      this.$api.events.fetch(null, 'cancelled').then((response) => {
        this.grouped_events.cancelled.events = response.events;
        this.eventStore.setCancelledEvents(response.events);
        this.grouped_events.cancelled.loading = false;
      });
    },
    showSuccessMessage(event, action) {
      this.showEventUpdateSuccessMessage = true
      this.editSuccessMessage= `Your event ${event.title} has been successfully ${action}`
      setTimeout(() => {
        this.showEventUpdateSuccessMessage = false;
        this.editSuccessMessage = '';
      }, 5000); // 5000 milliseconds = 5 seconds
    },
    async fetchByType(type, event, action) {
      if (event) this.showSuccessMessage(event, action);
      if (action === 'cancelled') await this.fetchCancelledEvents()
      if (action === 'duplicated') await this.fetchUpcomingEvents()
      switch (type) {
        case 'upcoming': this.fetchUpcomingEvents();break;
        case 'past': this.fetchPastEvents();break;
        case 'cancelled': this.fetchCancelledEvents();break;
      }
    },
    fetchEvents() {
      // loading upcoming events asynchronously
      if (!this.eventStore.upcoming.length > 0) {
        this.fetchUpcomingEvents();
      } else {
        this.grouped_events.upcoming.events = this.eventStore.upcoming;
      }

      // loading past events asynchronously
      if (!this.eventStore.past.length > 0) {
        this.fetchPastEvents()
      } else {
        this.grouped_events.past.events = this.eventStore.past;
      }

      // loading cancelled events asynchronously
      if (!this.eventStore.cancelled.length > 0) {
        this.fetchCancelledEvents()
      } else {
        this.grouped_events.cancelled.events = this.eventStore.cancelled;
      }
    },
    sortByDateAscending(a, b) {
      return new Date(a.start_datetime) - new Date(b.start_datetime);
    },
    sortByDateDescending(a, b) {
      return new Date(b.start_datetime) - new Date(a.start_datetime);
    },
    sortByNameAscending(a, b) {
      const nameA = a.title.toUpperCase();
      const nameB = b.title.toUpperCase();
      return nameA.localeCompare(nameB);
    },
    sortByStatus(a, b) {
      const nameA = a.status.toUpperCase();
      const nameB = b.status.toUpperCase();
      return nameA.localeCompare(nameB);
    },
    sortByDateThenNameAscending(a, b) {
      const dateComparison = this.sortByDateAscending(a, b);
      if (dateComparison !== 0) return dateComparison;
      return this.sortByNameAscending(a, b);
    },
    sortPastEvents(a, b) {
      const dateComparison = this.sortByDateDescending(a, b);
      if (dateComparison !== 0) return dateComparison;
      return this.sortByNameAscending(a, b);
    },
    getSortFunction(eventType) {
      if (this.selectedSort.id === 'date') {
        if (eventType === 'upcoming') {
          return this.sortByDateThenNameAscending;
        } else if (eventType === 'past') {
          return this.sortPastEvents;
        }
      } else if (this.selectedSort.id === 'alphabetically') {
        return this.sortByNameAscending;
      } else if (this.selectedSort.id === 'status') {
        return this.sortByStatus;
      }
    },
    toggleCreateDialog(data) {
      this.eventTitle = data?.title;
      this.showCreateDialog = !this.showCreateDialog;
      if (!this.showCreateDialog) {
        this.fetchEvents();
        if (data?.eventCreated) {
          this.showEventCreationSuccessMessage = true;
          setTimeout(() => {
            this.showEventCreationSuccessMessage = false;
          }, 3000); // 3000 milliseconds = 3 seconds
        }
      }
    },
    clearAll() {
      for (const [key, filter] of Object.entries(this.filters)) {
        if (!this.selectedFilters[key]) {
          continue;
        }
        this.selectedFilters[key] = filter.type == 'checkbox' ? [] : undefined;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.chipFilter:deep(.v-card__actions li) {
  padding-right: 20px;
}

.tab-navigation:deep {
  .v-tabs-items {
    background-color: transparent;
  }

  >.v-window {
    /* Allow space for the open filters */
    min-height: 550px;

    .v-menu__content {
      max-height: 450px !important;
    }
  }
}

.v-expansion-panels {
  border-radius: 0 0 4px 4px;

  &.wideActions .v-data-table:deep(td:last-child) {
    min-width: 134px;
  }
}

.v-data-table {
  .v-chip {
    width: 145px;
    height: 23px;
  }

  &:deep {
    th {
      font-size: 14px !important;
    }

    th,
    td:first-child,
    /* Date */
    td:nth-child(2) {
      /* Time */
      white-space: nowrap;
    }

    td:nth-child(3) {
      word-break: break-word;
    }

    th span {
      display: inline-block;
      white-space: pre-wrap;
    }
  }
}
</style>
